import React from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { UseQueryResult } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";

import { assert } from "../../../../commons/libs/assert";
import {
  getSupplierAvailabilityState,
  getWawiAvailabilityState,
  getWawiLocationAvailabilityState
} from "../../../../commons/libs/availability";
import envelope from "../../../../commons/libs/externals/envelope";
import { byDefinedKeys } from "../../../../commons/libs/filter-guards";
import { formatDate } from "../../../../commons/libs/formatters";
import { getProductImageFile, getProductImageUrl } from "../../../../commons/libs/resource-paths";
import {
  calcRelevantVariantSpecKeys,
  filterVariantSpecKeys,
  getProductVariants,
  getStandaloneProductVariant
} from "../../../../commons/libs/specs";
import { getSpecConfig } from "../../../../commons/specs";
import { Product, ProductKey, ProductSpecKey, StandaloneProductVariant } from "../../../../commons/specs/product";
import { AvailabilityType } from "../../../../commons/types/availability";
import { GetPrintItem, PrintType } from "../../../../commons/types/print-mail";
import { AvailabilityPrioritization } from "../../../../commons/types/settings";
import { VeloconnectErrorType } from "../../../../commons/types/veloconnect";
import { GetWawiProductAvailabiltyResponse, WawiErrorType } from "../../../../commons/types/wawi";
import * as icons from "../../../../resources/icons";
import zoomAnimationData from "../../../../resources/images/lottie/zoom-animation.json";
import Availability from "../../../commons/components/Availability/Availability";
import AvailabilityList from "../../../commons/components/AvailabilityList/AvailabilityList";
import Button from "../../../commons/components/Button/Button";
import CircularButton from "../../../commons/components/CircularButton/CircularButton";
import DescriptionList from "../../../commons/components/DescriptionList/DescriptionList";
import FoldoutDescription from "../../../commons/components/FoldoutDescription/FoldoutDescription";
import Headline from "../../../commons/components/Headline/Headline";
import Icon from "../../../commons/components/Icon/Icon";
import LoadingIndicator from "../../../commons/components/LoadingIndicator/LoadingIndicator";
import Paragraph from "../../../commons/components/Paragraph/Paragraph";
import Tooltip from "../../../commons/components/Tooltip/Tooltip";
import VariantAvailabilityError from "../../../commons/components/VariantAvailabilityError/VariantAvailabilityError";
import VariantAnnotation from "../../../commons/components/VariantInformationBox/VariantAnnotation";
import VariantInformationBox from "../../../commons/components/VariantInformationBox/VariantInformationBox";
import ZoomImageModal from "../../../commons/components/ZoomImageModal/ZoomImageModal";
import { ROUTES } from "../../../commons/routes";
import getBikeCenterName from "../../libs/bike-center-name";
import { useAppHistory } from "../../libs/hooks/use-app-routing";
import usePrintOrMail from "../../libs/hooks/use-print-or-mail";
import useSpecFormatter from "../../libs/hooks/use-spec-formatter";
import { useSettings } from "../../libs/queries/use-settings";
import { UseVeloconnectProductAvailabilitiesReturn } from "../../libs/queries/use-veloconnect-product-availabilities";
import { useCommonAppDispatcher, useCommonAppSelector } from "../../store/hooks";
import ModalContainerWithStatePartial from "../Modal/ModalContainerWithStatePartial";
import { useVeloconnectEndpointContext } from "../utils/veloconnect-endpoint-context";

const zoomAnimationDataString = JSON.stringify(zoomAnimationData);

interface Props {
  product: Product;
  productVariant: StandaloneProductVariant;
  getPrintItem?: GetPrintItem;
  hasInternetConnectivity: boolean;
  isVeloconnectActive: boolean;
  veloconnectAvailabilities?: UseVeloconnectProductAvailabilitiesReturn;
  veloconnectError?: VeloconnectErrorType;
  isWawiActive: boolean;
  wawiProductAvailabilities?: UseQueryResult<GetWawiProductAvailabiltyResponse>;
  wawiError?: WawiErrorType;
}

const VariantInformationBoxPartial = ({
  product,
  productVariant,
  getPrintItem,
  hasInternetConnectivity,
  isVeloconnectActive,
  veloconnectAvailabilities,
  veloconnectError,
  isWawiActive,
  wawiProductAvailabilities,
  wawiError
}: Props) => {
  const { t, i18n } = useTranslation(["commons"]);
  const history = useAppHistory();

  const showProductDetailsZoomNotice = useCommonAppSelector(state => state.session.showProductDetailsZoomNotice);
  const [dispatch, actions] = useCommonAppDispatcher();
  const hideProductDetailsZoomNotice = () => dispatch(actions.session.hideProductDetailsZoomNotice());

  const settings = useSettings();
  const veloconnectEndpoint = useVeloconnectEndpointContext();
  const printOrMail = usePrintOrMail();

  const locale = i18n.language;
  const { isMobileBuild } = envelope;

  const productType = product[ProductKey.ProductType];
  const specConfig = getSpecConfig(productType);
  const specFormatter = useSpecFormatter("commons", productType, settings.initialized.customization);

  const productVariants = getProductVariants(product);
  const variantFromProduct = productVariants.find(
    variant => variant[ProductKey.VariantId] === productVariant[ProductKey.VariantId]
  );
  assert(variantFromProduct, "Variant not found on product");

  const relevantVariantSpecKeys = calcRelevantVariantSpecKeys(
    [productVariants.length > 1 ? variantFromProduct : getStandaloneProductVariant(product, variantFromProduct)],
    specConfig.specDefinitions,
    "variantInformationBox",
    specConfig.variantInformationBoxSpecKeyPriorityList
  );

  const selectedVeloconnectProductAvailabilities = (veloconnectAvailabilities?.query.data || []).find(
    variant => variant.variantId === productVariant[ProductKey.VariantId]
  );
  const selectedWawiProductAvailabilitiesVariant = (wawiProductAvailabilities?.data?.variants ?? []).find(
    variant => variant.variantId === productVariant[ProductKey.VariantId]
  );

  const firstVeloconnectAvailability = selectedVeloconnectProductAvailabilities?.availabilities[0];

  const wawiAvailabilityState =
    selectedWawiProductAvailabilitiesVariant &&
    getWawiAvailabilityState(selectedWawiProductAvailabilitiesVariant, {
      preferSupplier:
        settings.initialized.customization.availabilityPrioritizationOptions?.availabilityPrioritization ===
        AvailabilityPrioritization.Supplier
    });

  const supplierAvailabilityState = getSupplierAvailabilityState(
    selectedWawiProductAvailabilitiesVariant,
    selectedVeloconnectProductAvailabilities
  );

  const handleLinkClick = (_event: React.MouseEvent<HTMLElement>, href: string) => {
    history.push(href);
  };

  const renderSupplierAnnotation = () => {
    const renderAvailability = () => {
      if (wawiProductAvailabilities?.isLoading || veloconnectAvailabilities?.query.isFetching) {
        return (
          <LoadingIndicator
            size="s"
            label={t("commons:variantInformationBoxPartialProductDetails.supplierAvailability.loadingLabel")}
          />
        );
      }

      if (!veloconnectEndpoint.query.data) {
        return (
          <Availability
            kind={AvailabilityType.Unknown}
            label={t("commons:variantsContentPartialProductDetails.availabilityStatus.noAvailabilityData")}
          />
        );
      } else {
        if (!veloconnectEndpoint.query.data?.isConfigured) {
          return (
            <Availability
              kind={AvailabilityType.Unknown}
              label={t("commons:variantsContentPartialProductDetails.availabilityStatus.noAvailabilityData")}
            />
          );
        } else if (veloconnectEndpoint.query.data?.areCredentialsInvalid) {
          return (
            <Availability
              kind={AvailabilityType.Error}
              label={t(
                "commons:variantInformationBoxPartialProductDetails.supplierAvailability.invalidCredentialsLabel"
              )}
            />
          );
        } else if (!veloconnectEndpoint.isImportReady) {
          return (
            <Availability
              kind={AvailabilityType.Error}
              label={t("commons:variantInformationBoxPartialProductDetails.supplierAvailability.importIsNotReadyYet")}
            />
          );
        } else if (veloconnectEndpoint.isImportOutdated) {
          return (
            <Availability
              kind={AvailabilityType.Error}
              label={t("commons:variantInformationBoxPartialProductDetails.supplierAvailability.lastImportIsOutdated")}
            />
          );
        } else if (supplierAvailabilityState) {
          return (
            <Availability
              kind={supplierAvailabilityState.type}
              label={t(
                `commons:variantsContentPartialProductDetails.availabilityStatus.${supplierAvailabilityState.status}`,
                {
                  expectedDeliveryDate:
                    supplierAvailabilityState.expectedDeliveryDate &&
                    formatDate(supplierAvailabilityState.expectedDeliveryDate, locale),
                  warehouseLocation: supplierAvailabilityState.warehouseLocation
                }
              )}
            />
          );
        }
      }
    };

    const renderTooltipContent = () => {
      if (!veloconnectEndpoint.query.data) {
        return (
          <VariantAvailabilityError
            kind={AvailabilityType.Unknown}
            icon={<Icon block source={icons.IconSmallAttention} />}
            headline={
              <Headline kind="xs">
                {t(
                  "commons:variantInformationBoxPartialProductDetails.supplierTooltip.availabilityNotPossibleHeadline"
                )}
              </Headline>
            }
            paragraph={
              <>
                <Paragraph kind="support" classNames={["u-space-base"]}>
                  <Trans
                    t={t}
                    i18nKey="commons:variantInformationBoxPartialProductDetails.supplierTooltip.availabilityNotPossible"
                    components={{ linebreak: <br /> }}
                    values={{ brandName: product[ProductSpecKey.BrandName] }}
                  />
                </Paragraph>
              </>
            }
          />
        );
      } else {
        if (!veloconnectEndpoint.query.data?.isConfigured) {
          return (
            <VariantAvailabilityError
              kind={AvailabilityType.Unknown}
              icon={<Icon block source={icons.IconSmallAttention} />}
              headline={
                <Headline kind="xs">
                  {t(
                    "commons:variantInformationBoxPartialProductDetails.supplierTooltip.availabilityIsNotConfiguredHeadline"
                  )}
                </Headline>
              }
              paragraph={
                <>
                  <Paragraph kind="support" classNames={["u-space-base"]}>
                    <Trans
                      t={t}
                      i18nKey="commons:variantInformationBoxPartialProductDetails.supplierTooltip.availabilityIsNotConfigured"
                      components={{ linebreak: <br /> }}
                      values={{ brandName: product[ProductSpecKey.BrandName] }}
                    />
                  </Paragraph>
                  {!isMobileBuild && (
                    <Button
                      iconRight
                      size="s"
                      icon={<Icon source={icons.IconSmallArrowRight} />}
                      onClick={event => handleLinkClick(event, ROUTES.SETTINGS.VELOCONNECT)}
                    >
                      {t(
                        "commons:variantInformationBoxPartialProductDetails.supplierTooltip.availabilityNotPossibleButton"
                      )}
                    </Button>
                  )}
                </>
              }
            />
          );
        } else if (veloconnectEndpoint.query.data?.areCredentialsInvalid) {
          return (
            <VariantAvailabilityError
              kind={AvailabilityType.Error}
              icon={<Icon block source={icons.IconSmallAttention} />}
              headline={
                <Headline kind="xs">
                  {t("commons:variantInformationBoxPartialProductDetails.supplierTooltip.invalidCredentialsHeadline")}
                </Headline>
              }
              paragraph={
                <>
                  <Paragraph kind="support" classNames={["u-space-base"]}>
                    <Trans
                      t={t}
                      i18nKey="commons:variantInformationBoxPartialProductDetails.supplierTooltip.credentialsExpired"
                      components={{ linebreak: <br /> }}
                    />
                  </Paragraph>
                  {!isMobileBuild && (
                    <Button
                      iconRight
                      size="s"
                      icon={<Icon source={icons.IconSmallArrowRight} />}
                      onClick={event => handleLinkClick(event, ROUTES.SETTINGS.VELOCONNECT)}
                    >
                      {t("commons:variantInformationBoxPartialProductDetails.supplierTooltip.checkCredentialsButton")}
                    </Button>
                  )}
                </>
              }
            />
          );
        } else if (!veloconnectEndpoint.isImportReady) {
          return (
            <VariantAvailabilityError
              kind={AvailabilityType.Error}
              icon={<Icon block source={icons.IconSmallAttention} />}
              headline={
                <Headline kind="xs">
                  {t("commons:variantInformationBoxPartialProductDetails.supplierTooltip.importIsNotReadyYetHeadline")}
                </Headline>
              }
              paragraph={
                <>
                  <Paragraph kind="support" classNames={["u-space-base"]}>
                    <Trans
                      t={t}
                      i18nKey="commons:variantInformationBoxPartialProductDetails.supplierTooltip.importIsNotReadyYetExplanation"
                      components={{ linebreak: <br /> }}
                    />
                  </Paragraph>
                  {!isMobileBuild && (
                    <Button
                      iconRight
                      size="s"
                      icon={<Icon source={icons.IconSmallArrowRight} />}
                      onClick={event => handleLinkClick(event, ROUTES.SETTINGS.SUPPORT)}
                    >
                      {t(
                        "commons:variantInformationBoxPartialProductDetails.supplierTooltip.importIsNotReadyYetContactSupportButton"
                      )}
                    </Button>
                  )}
                </>
              }
            />
          );
        } else if (veloconnectEndpoint.isImportOutdated) {
          return (
            <VariantAvailabilityError
              kind={AvailabilityType.Error}
              icon={<Icon block source={icons.IconSmallAttention} />}
              headline={
                <Headline kind="xs">
                  {t("commons:variantInformationBoxPartialProductDetails.supplierTooltip.lastImportOutdatedHeadline")}
                </Headline>
              }
              paragraph={
                <>
                  <Paragraph kind="support" classNames={["u-space-base"]}>
                    <Trans
                      t={t}
                      i18nKey="commons:variantInformationBoxPartialProductDetails.supplierTooltip.lastImportOutdatedExplanation"
                      components={{ linebreak: <br /> }}
                    />
                  </Paragraph>
                  {!isMobileBuild && (
                    <Button
                      iconRight
                      size="s"
                      icon={<Icon source={icons.IconSmallArrowRight} />}
                      onClick={event => handleLinkClick(event, ROUTES.SETTINGS.SUPPORT)}
                    >
                      {t(
                        "commons:variantInformationBoxPartialProductDetails.supplierTooltip.lastImportOutdatedContactSupportButton"
                      )}
                    </Button>
                  )}
                </>
              }
            />
          );
        } else if (supplierAvailabilityState?.itemUnknown) {
          return (
            <VariantAvailabilityError
              kind={supplierAvailabilityState?.type}
              icon={<Icon block source={icons.IconSmallAttention} />}
              headline={
                <Headline kind="xs">
                  {t("commons:variantInformationBoxPartialProductDetails.supplierTooltip.productNotFoundHeadline")}
                </Headline>
              }
              paragraph={
                <Paragraph kind="support">
                  <Trans
                    t={t}
                    i18nKey="commons:variantInformationBoxPartialProductDetails.supplierTooltip.productNotFound"
                    components={{ linebreak: <br /> }}
                  />
                </Paragraph>
              }
            />
          );
        }
      }
    };

    const tooltipContent = renderTooltipContent();

    const isConfiguredButHasFaults = !!(
      veloconnectEndpoint.query.data &&
      veloconnectEndpoint.query.data.isConfigured &&
      (veloconnectEndpoint.query.data.areCredentialsInvalid ||
        !veloconnectEndpoint.isImportReady ||
        veloconnectEndpoint.isImportOutdated)
    );

    return (
      <VariantAnnotation
        label={t("commons:variantInformationBoxPartialProductDetails.supplierLabel")}
        availability={renderAvailability()}
        icon={!!tooltipContent ? <Icon source={icons.IconSmallAttention} /> : undefined}
        tooltipContent={tooltipContent}
        error={(hasInternetConnectivity && isConfiguredButHasFaults) || !!veloconnectError}
      />
    );
  };

  const renderWawiAnnotation = () => {
    const renderAvailability = () => {
      if (wawiError === WawiErrorType.Other) {
        return (
          <Availability
            kind={AvailabilityType.Error}
            label={t("commons:variantInformationBoxPartialProductDetails.erpAvailability.serverNotReachableLabel")}
          />
        );
      } else if (wawiError === WawiErrorType.InvalidProductIdentification) {
        return (
          <Availability
            kind={AvailabilityType.Error}
            label={t("commons:variantInformationBoxPartialProductDetails.erpAvailability.insufficientDataQualityLabel")}
          />
        );
      } else if (wawiError === WawiErrorType.InvalidCredentials) {
        return (
          <Availability
            kind={AvailabilityType.Error}
            label={t("commons:variantInformationBoxPartialProductDetails.erpAvailability.invalidCredentialsLabel")}
          />
        );
      } else if (!selectedWawiProductAvailabilitiesVariant) {
        return (
          <Availability
            kind={AvailabilityType.Unknown}
            label={t("commons:variantInformationBoxPartialProductDetails.erpAvailability.noAvailabilityDataLabel")}
          />
        );
      } else if (wawiAvailabilityState) {
        return (
          <Availability
            kind={wawiAvailabilityState.type}
            label={t(
              `commons:variantsContentPartialProductDetails.availabilityStatus.${wawiAvailabilityState.status}`,
              {
                expectedDeliveryDate:
                  wawiAvailabilityState.expectedDeliveryDate &&
                  formatDate(wawiAvailabilityState.expectedDeliveryDate, locale),
                warehouseLocation: wawiAvailabilityState.warehouseLocation
              }
            )}
          />
        );
      } else {
        return (
          <LoadingIndicator
            size="s"
            label={t("commons:variantInformationBoxPartialProductDetails.erpAvailability.loadingLabel")}
          />
        );
      }
    };

    const renderTooltipContent = () => {
      if (wawiError === WawiErrorType.Other) {
        return (
          <VariantAvailabilityError
            kind={AvailabilityType.Error}
            icon={<Icon block source={icons.IconSmallAttention} />}
            headline={
              <Headline kind="xs">
                {t("commons:variantInformationBoxPartialProductDetails.erpTooltip.serverNotReachableHeadline")}
              </Headline>
            }
            paragraph={
              <Paragraph kind="support">
                <Trans
                  t={t}
                  i18nKey="commons:variantInformationBoxPartialProductDetails.erpTooltip.availabilityRequestUnavailable"
                  components={{ linebreak: <br /> }}
                />
              </Paragraph>
            }
          />
        );
      } else if (wawiError === WawiErrorType.InvalidProductIdentification) {
        return (
          <VariantAvailabilityError
            kind={AvailabilityType.Error}
            icon={<Icon block source={icons.IconSmallSmileySad} />}
            headline={
              <Headline kind="xs">
                {t("commons:variantInformationBoxPartialProductDetails.erpTooltip.insufficientDataQualityHeadline")}
              </Headline>
            }
            paragraph={
              <Paragraph kind="support">
                {t("commons:variantInformationBoxPartialProductDetails.erpTooltip.insufficientDataQuality", {
                  brandName: specFormatter?.formatVariantValue(ProductSpecKey.BrandName, productVariant)
                })}
              </Paragraph>
            }
          />
        );
      } else if (wawiError === WawiErrorType.InvalidCredentials) {
        return (
          <VariantAvailabilityError
            kind={AvailabilityType.Error}
            icon={<Icon block source={icons.IconSmallAttention} />}
            headline={
              <Headline kind="xs">
                {t("commons:variantInformationBoxPartialProductDetails.erpTooltip.invalidCredentialsHeadline")}
              </Headline>
            }
            paragraph={
              <>
                <Paragraph kind="support" classNames={["u-space-base"]}>
                  <Trans
                    t={t}
                    i18nKey="commons:variantInformationBoxPartialProductDetails.erpTooltip.credentialsExpired"
                    components={{ linebreak: <br /> }}
                  />
                </Paragraph>
                {!isMobileBuild && (
                  <Button
                    iconRight
                    size="s"
                    icon={<Icon source={icons.IconSmallArrowRight} />}
                    onClick={event => handleLinkClick(event, ROUTES.SETTINGS.WAWI_INDEX)}
                  >
                    {t("commons:variantInformationBoxPartialProductDetails.erpTooltip.checkCredentialsButton")}
                  </Button>
                )}
              </>
            }
          />
        );
      } else if (selectedWawiProductAvailabilitiesVariant?.itemAmbiguous) {
        return (
          <VariantAvailabilityError
            kind={wawiAvailabilityState && wawiAvailabilityState.type}
            icon={<Icon block source={icons.IconSmallAttention} />}
            headline={
              <Headline kind="xs">
                {t("commons:variantInformationBoxPartialProductDetails.erpTooltip.foundMultipleHeadline")}
              </Headline>
            }
            paragraph={
              <Paragraph kind="support">
                <Trans
                  t={t}
                  i18nKey="commons:variantInformationBoxPartialProductDetails.erpTooltip.foundProductMultipleTimes"
                  components={{ linebreak: <br /> }}
                />
              </Paragraph>
            }
          />
        );
      } else if (!selectedWawiProductAvailabilitiesVariant || selectedWawiProductAvailabilitiesVariant?.itemUnknown) {
        return (
          <VariantAvailabilityError
            kind={wawiAvailabilityState && wawiAvailabilityState.type}
            icon={<Icon block source={icons.IconSmallAttention} />}
            headline={
              <Headline kind="xs">
                {t("commons:variantInformationBoxPartialProductDetails.erpTooltip.productNotFoundHeadline")}
              </Headline>
            }
            paragraph={
              <Paragraph kind="support">
                <Trans
                  t={t}
                  i18nKey="commons:variantInformationBoxPartialProductDetails.erpTooltip.productNotFound"
                  components={{ linebreak: <br /> }}
                />
              </Paragraph>
            }
          />
        );
      } else if (selectedWawiProductAvailabilitiesVariant) {
        const availableWawiProductVariants = selectedWawiProductAvailabilitiesVariant.availabilities.filter(
          ({ productCount, orderedCount }) => productCount > 0 || (orderedCount && orderedCount > 0)
        );

        if (availableWawiProductVariants.length > 0) {
          return (
            <AvailabilityList>
              {availableWawiProductVariants
                .map(availability => getWawiLocationAvailabilityState(availability))
                .map((transformed, index) => (
                  <Availability
                    key={index}
                    kind={transformed.type}
                    label={t(`commons:variantsContentPartialProductDetails.availabilityStatus.${transformed.status}`, {
                      expectedDeliveryDate:
                        transformed.expectedDeliveryDate && formatDate(transformed.expectedDeliveryDate, locale),
                      warehouseLocation: transformed.warehouseLocation
                    })}
                    block
                  />
                ))}
            </AvailabilityList>
          );
        } else {
          return (
            <VariantAvailabilityError
              kind={wawiAvailabilityState && wawiAvailabilityState.type}
              icon={<Icon block source={icons.IconSmallAttention} />}
              headline={
                <Headline kind="xs">
                  {t("commons:variantInformationBoxPartialProductDetails.erpTooltip.productNotAvailableHeadline")}
                </Headline>
              }
              paragraph={
                <Paragraph kind="support">
                  {t("commons:variantInformationBoxPartialProductDetails.erpTooltip.productNotAvailable")}
                </Paragraph>
              }
            />
          );
        }
      }
    };

    const tooltipContent = renderTooltipContent();

    return (
      <VariantAnnotation
        label={t("commons:variantInformationBoxPartialProductDetails.localLabel")}
        availability={renderAvailability()}
        icon={!!tooltipContent ? <Icon source={icons.IconSmallAttention} /> : undefined}
        tooltipContent={tooltipContent}
      />
    );
  };

  const renderOfflineAnnotation = () => {
    return (
      <VariantAnnotation
        error
        label={t("commons:variantInformationBoxPartialProductDetails.offlineLabel")}
        availability={
          <Availability
            kind={AvailabilityType.Error}
            label={t("commons:variantInformationBoxPartialProductDetails.deviceOfflineLabel")}
          />
        }
        icon={<Icon source={icons.IconSmallAttention} />}
        tooltipContent={
          <VariantAvailabilityError
            kind={AvailabilityType.Error}
            icon={<Icon block source={icons.IconSmallOffline} />}
            headline={
              <Headline kind="xs">
                {t("commons:variantInformationBoxPartialProductDetails.offlineTooltip.offlineHeadline")}
              </Headline>
            }
            paragraph={
              <Paragraph kind="support">
                <Trans
                  t={t}
                  i18nKey="commons:variantInformationBoxPartialProductDetails.offlineTooltip.deviceOfflineRequestNotPossible"
                  components={{ linebreak: <br /> }}
                />
              </Paragraph>
            }
          />
        }
      />
    );
  };

  const renderAuthOrOtherErrorAnnotation = () => {
    return (
      <VariantAnnotation
        error
        label={t("commons:variantInformationBoxPartialProductDetails.supplierLabel")}
        availability={
          <Availability
            kind={AvailabilityType.Error}
            label={t("commons:variantInformationBoxPartialProductDetails.authOrOtherErrorAvailability.label")}
          />
        }
        icon={<Icon source={icons.IconSmallAttention} />}
        tooltipContent={
          <VariantAvailabilityError
            kind={AvailabilityType.Error}
            icon={<Icon block source={icons.IconSmallOffline} />}
            headline={
              <Headline kind="xs">
                {t("commons:variantInformationBoxPartialProductDetails.authOrOtherErrorTooltip.headline")}
              </Headline>
            }
            paragraph={
              <>
                <Paragraph kind="support" classNames={["u-space-base"]}>
                  <Trans
                    t={t}
                    i18nKey="commons:variantInformationBoxPartialProductDetails.authOrOtherErrorTooltip.explanation"
                    components={{ linebreak: <br /> }}
                  />
                </Paragraph>
                {!isMobileBuild && (
                  <Button
                    iconRight
                    size="s"
                    icon={<Icon source={icons.IconSmallArrowRight} />}
                    onClick={event => handleLinkClick(event, ROUTES.SETTINGS.SUPPORT)}
                  >
                    {t("commons:variantInformationBoxPartialProductDetails.authOrOtherErrorTooltip.button")}
                  </Button>
                )}
              </>
            }
          />
        }
      />
    );
  };

  const renderTopAnnotation = () => {
    if (!hasInternetConnectivity) {
      return renderOfflineAnnotation();
    } else if (
      veloconnectError === VeloconnectErrorType.AuthorizationFailed ||
      veloconnectError === VeloconnectErrorType.Other
    ) {
      return renderAuthOrOtherErrorAnnotation();
    } else if ((isVeloconnectActive || isWawiActive) && supplierAvailabilityState.type !== AvailabilityType.Unknown) {
      return renderSupplierAnnotation();
    } else {
      return null;
    }
  };

  const renderBottomAnnotation = () => {
    if (!hasInternetConnectivity) {
      return null;
    } else if (isWawiActive) {
      return renderWawiAnnotation();
    } else {
      return null;
    }
  };

  const hasVariantImage = Boolean(getProductImageFile(productVariant, false));
  const productImageUrl = getProductImageUrl(product);
  const resolvedImageUrl = hasVariantImage ? getProductImageUrl(productVariant, {}, false) : productImageUrl;

  return (
    <ModalContainerWithStatePartial
      modal={(_isOpen, close) => (
        <ZoomImageModal
          openButton={null}
          imageUrl={resolvedImageUrl}
          notice={
            <Tooltip
              variant="accent"
              maxWidth="s"
              content={t("productDetailsImagePartialProductDetails.zoomNotice")}
              openOnMount
              isCloseable
              openOnMountDelay={300}
              shouldCloseAutomatically={false}
              onClose={hideProductDetailsZoomNotice}
            >
              <DotLottieReact loop autoplay data={zoomAnimationDataString} style={{ height: 240, width: 240 }} />
            </Tooltip>
          }
          onClose={close}
          showNotice={showProductDetailsZoomNotice}
          onHideNotice={hideProductDetailsZoomNotice}
        />
      )}
    >
      {(_isOpen, open) => (
        <VariantInformationBox
          imgSource={resolvedImageUrl}
          fallbackSource={productImageUrl}
          topAnnotation={renderTopAnnotation()}
          bottomAnnotation={renderBottomAnnotation()}
          onImageClick={open}
          imageAction={
            <CircularButton variant="primary" elevated onClick={open}>
              <Icon source={icons.IconSmallZoomIn} />
            </CircularButton>
          }
          button={
            getPrintItem &&
            (printOrMail.isPrintingActive || printOrMail.isMailActive) && (
              <Button
                size="s"
                variant="accent"
                kind="solid"
                onClick={() => {
                  printOrMail.printOrMail({
                    type: PrintType.ProductDataSheet,
                    bikeCenterName: getBikeCenterName(settings.initialized.customization),
                    showPrices: settings.initialized.customization.showPrices,
                    showAssortmentPrices: settings.assortmentPriceSettings?.showAssortmentPrices ?? false,
                    showFallbackPrices: settings.assortmentPriceSettings?.showFallbackPrices ?? false,
                    showOriginalPrices: settings.assortmentPriceSettings?.showOriginalPrices ?? false,
                    useRrpAsOriginalPrice: settings.assortmentPriceSettings?.useRrpAsOriginalPrice ?? true,
                    showSaleBadge: settings.assortmentPriceSettings?.showSaleBadge ?? null,
                    concealModelYear: settings.initialized.customization.modelYearDisplay.concealed,
                    modelYearDisplayTerm: settings.initialized.customization.modelYearDisplay.term,
                    currency: settings.initialized.currency,
                    items: [
                      getPrintItem({
                        variantId: productVariant[ProductKey.VariantId],
                        product
                      })
                    ],
                    initiator: "VariantInformationBox"
                  });
                }}
              >
                {printOrMail.isPrintingActive && !printOrMail.isMailActive
                  ? t("commons:variantInformationBoxPartialProductDetails.printVariantButton")
                  : t("commons:variantInformationBoxPartialProductDetails.shareVariantButton")}
              </Button>
            )
          }
        >
          {firstVeloconnectAvailability?.description && (
            <DescriptionList>
              <dt>{t("commons:variantInformationBoxPartialProductDetails.productInformationManufacturer")}</dt>
              <dd>
                <FoldoutDescription
                  lines={4}
                  labelButton={t("commons:variantInformationBoxPartialProductDetails.moreButton")}
                >
                  {firstVeloconnectAvailability.description}
                </FoldoutDescription>
              </dd>
            </DescriptionList>
          )}
          <DescriptionList>
            {filterVariantSpecKeys(relevantVariantSpecKeys, settings.initialized.customization.showPrices)
              .map(specKey => ({
                specKey,
                label: specFormatter?.formatLabel(specKey),
                value: specFormatter?.formatVariantValue(specKey, productVariant)
              }))
              .filter(byDefinedKeys("value", "label"))
              .map(({ specKey, label, value }) => (
                <React.Fragment key={specKey}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </React.Fragment>
              ))}
          </DescriptionList>
        </VariantInformationBox>
      )}
    </ModalContainerWithStatePartial>
  );
};

export default VariantInformationBoxPartial;
